import React, { useContext } from "react";
import { Popover, OverlayTrigger } from "react-bootstrap";
import moment from "moment";
import { editDaypartingRules } from "@ts/day-parting";
import { mainContext } from "@ts/constants";
import { CLOCK_COLORS, DAY_PARTING_DAYS } from "@ts/constants";
import { ReactComponent as ClockIcon } from "@images/clock_icon.svg";
import { IDprRange, IMainContext } from "@ts/common-types";
import { ICellRendererParams } from "ag-grid-community";

type TDayPartingSlotPopover = {
    params: ICellRendererParams;
    automationIconColor: string;
};

const parseTime = (time: string) => {
    return parseInt(time.replace(":", ""));
};

const DayPartingSlotPopover: React.FC<TDayPartingSlotPopover> = ({
    params,
    automationIconColor,
}) => {
    const { dprTimeSlots, setSelectedValue } =
        useContext<IMainContext>(mainContext);
    const currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const dprTimezone =
        params.data.dpr_timezone !== null
            ? params.data.dpr_timezone
            : currentTimezone;

    const currentTime = moment(
        new Date().toLocaleString("en-US", { timeZone: dprTimezone }),
        "M/D/YYYY, h:mm:ss A"
    );
    const currentDayIndex = (
        currentTime.day() === 0 ? 7 : currentTime.day()
    ).toString();
    const slotsToDisplay = () => {
        const allSlots = params.data.all_dpr_slots;
        let dayIndex;
        const currentDaySlots: Array<IDprRange> =
            currentDayIndex in allSlots ? allSlots[currentDayIndex].slots : [];

        let slots = [...currentDaySlots];
        dayIndex = currentDayIndex;

        if (slots.length === 0 || !allSlots[currentDayIndex].state) {
            const availableSlots = Object.keys(
                params.data.all_dpr_slots
            ).filter((key) => key !== currentDayIndex);
            const nextSlotArr = availableSlots.filter(
                (slot) => slot > currentDayIndex
            );
            let nextSlot = "";
            if (nextSlotArr.length !== 0) {
                nextSlotArr.forEach((slot, index, arr) => {
                    if (allSlots[slot].state) {
                        nextSlot = slot;
                        arr.length = index + 1;
                    }
                });
            }

            if (
                nextSlot === "" &&
                availableSlots.length !== 0 &&
                availableSlots[0] !== currentDayIndex
            ) {
                availableSlots.forEach((slot, index, arr) => {
                    if (slot !== currentDayIndex && allSlots[slot].state) {
                        nextSlot = slot;
                        arr.length = index + 1;
                    }
                });
            }

            if (nextSlot !== "") {
                slots = [...allSlots[nextSlot].slots];
                dayIndex = nextSlot;
            } else {
                slots = [];
                dayIndex = currentDayIndex;
            }
        }

        slots.sort((prevSlot: IDprRange, nextSlot: IDprRange) => {
            if (
                parseTime(prevSlot.start) -
                    parseTime(moment().format("HH:mm")) <
                parseTime(nextSlot.start) - parseTime(moment().format("HH:mm"))
            ) {
                return -1;
            }
            if (
                parseTime(prevSlot.start) -
                    parseTime(moment().format("HH:mm")) >
                parseTime(nextSlot.start) - parseTime(moment().format("HH:mm"))
            ) {
                return 1;
            }
            return 0;
        });

        const nextSlotIndex =
            slots.length &&
            (dayIndex === currentDayIndex
                ? slots.indexOf(
                      slots.find(
                          (slot) =>
                              parseTime(slot.start) -
                                  parseTime(currentTime.format("HH:mm")) >
                              0
                      )
                  )
                : 0);

        return {
            slots,
            dayIndex,
            nextSlotIndex: nextSlotIndex === -1 ? 0 : nextSlotIndex,
        };
    };

    const getNextSlot = () => {
        const nextSlotsData = slotsToDisplay();
        const nextAvailableSlot =
            nextSlotsData.slots[nextSlotsData.nextSlotIndex];
        const nextDayIndex = nextSlotsData.dayIndex;

        let message;
        const nextTimeSlot = nextAvailableSlot.start.split(":");
        const arr = nextTimeSlot.map((t: string) => Number(t));
        const nextSlot = moment(arr, "HH:mm:ss");

        if (nextSlotsData.slots.length && nextDayIndex === currentDayIndex) {
            const hourDiff = +moment
                .duration(nextSlot.diff(currentTime))
                .asHours()
                .toString()
                .split(".")[0];
            const minDiff = +(
                moment.duration(nextSlot.diff(currentTime)).asMinutes() % 60
            )
                .toString()
                .split(".")[0];

            message =
                minDiff < 0 && hourDiff < 0 && params.data.state === "paused"
                    ? "Dayparting not applicable as it was already paused before the start time"
                    : minDiff < 0 && hourDiff < 0
                    ? "No active slots to pause"
                    : minDiff === 0 && hourDiff === 0
                    ? `Day parting about to begin`
                    : hourDiff === 0 && minDiff !== 0
                    ? `Next pause is after : ${minDiff} minutes`
                    : `Next pause is after : ${hourDiff} hours ${minDiff} mins`;
        } else {
            message =
                "Next pause is on " +
                DAY_PARTING_DAYS[Number(nextDayIndex) - 1];
        }

        return message;
    };

    return (
        <>
            {params.node.rowPinned ? null : (
                <OverlayTrigger
                    key={params.data.sys_campaign_id}
                    placement="right"
                    delay={{ show: 0, hide: 0 }}
                    overlay={
                        <Popover
                            id={`popover-positioned-right`}
                            style={{
                                display: `${
                                    automationIconColor === "grey" && "none"
                                }`,
                            }}
                        >
                            {automationIconColor === "green" ? (
                                <div>
                                    <Popover.Header
                                        as="h5"
                                        style={{
                                            fontSize: "12px",
                                            backgroundColor: "#FFFFFF",
                                        }}
                                    >
                                        Day Parting is active
                                    </Popover.Header>
                                </div>
                            ) : automationIconColor === "blue" ? (
                                <div>
                                    {slotsToDisplay().slots.length === 0 ? (
                                        <Popover.Header
                                            as="h5"
                                            style={{
                                                fontSize: "12px",
                                                backgroundColor: "#FFFFFF",
                                            }}
                                        >
                                            {`All schedules completed`}
                                        </Popover.Header>
                                    ) : (
                                        <>
                                            <Popover.Header
                                                as="h5"
                                                style={{
                                                    fontSize: "12px",
                                                    backgroundColor:
                                                        "rgba(255, 244, 210, 0.5)",
                                                    fontWeight: "700",
                                                }}
                                            >
                                                {getNextSlot()}
                                            </Popover.Header>
                                            <Popover.Body>
                                                <label
                                                    style={{
                                                        fontSize: ".7rem",
                                                        fontWeight: "900",
                                                        marginBottom: "5px",
                                                        display: `${
                                                            dprTimezone !==
                                                            currentTimezone
                                                                ? "block"
                                                                : "none"
                                                        }`,
                                                    }}
                                                >
                                                    {`${params.data.dpr_timezone}`}
                                                </label>
                                                {slotsToDisplay().slots.map(
                                                    (
                                                        slot: IDprRange,
                                                        index: number
                                                    ) => (
                                                        <div key={index}>
                                                            <strong
                                                                style={{
                                                                    display:
                                                                        "block",
                                                                    fontSize:
                                                                        "12px",
                                                                    color:
                                                                        getNextSlot() ===
                                                                        "No active slots to pause"
                                                                            ? "#000000"
                                                                            : index ===
                                                                              slotsToDisplay()
                                                                                  .nextSlotIndex
                                                                            ? "#F2921D"
                                                                            : "#000000",
                                                                }}
                                                            >
                                                                {slot.start} -{" "}
                                                                {slot.end}
                                                            </strong>
                                                        </div>
                                                    )
                                                )}
                                            </Popover.Body>
                                        </>
                                    )}
                                </div>
                            ) : automationIconColor === "red" ? (
                                <div>
                                    <Popover.Header
                                        as="h5"
                                        style={{
                                            fontSize: "12px",
                                            backgroundColor: "#FFFFFF",
                                        }}
                                    >
                                        Day Parting is disabled
                                    </Popover.Header>
                                </div>
                            ) : null}
                        </Popover>
                    }
                >
                    <button
                        className="day-parting-clock-btn"
                        onClick={() => {
                            if (
                                !(
                                    params.data.automation === null ||
                                    params.data.automation === ""
                                )
                            )
                                editDaypartingRules(
                                    [params.data.sys_campaign_id.toString()],
                                    dprTimeSlots,
                                    setSelectedValue
                                );
                        }}
                    >
                        <ClockIcon
                            fill={`${CLOCK_COLORS[automationIconColor]}`}
                            width="18"
                            height="18"
                        />
                    </button>
                </OverlayTrigger>
            )}
        </>
    );
};

export default DayPartingSlotPopover;
